import React from 'react';
import { AnimalType } from '../../models/animal';
import { MedicalSupplyType } from '../../models/injection';
import { Paper, TableRow, TableHead, TableCell, TableBody, Table, Chip, TableContainer } from '@material-ui/core';

import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainInjectionsResultSupplyInfo } from '../../api/HealthSystemApi';

interface VaccinationAndMedicationDataTableProps {
  animalType: AnimalType;
  supplyInfo: HealthSystemDomainInjectionsResultSupplyInfo[];
  showPerAnimalColumns?: boolean;
}

const VaccinationAndMedicationDataTable: React.FC<any> = ({
  animalType,
  supplyInfo,
  showPerAnimalColumns = true
}: VaccinationAndMedicationDataTableProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isSowOrGilt: boolean =
    showPerAnimalColumns && (animalType === AnimalType.Sow || animalType === AnimalType.Gilt);

  const getVaccinations = (): HealthSystemDomainInjectionsResultSupplyInfo[] => {
    return supplyInfo.filter((x) => x.supply.supplyType === MedicalSupplyType.Vaccination);
  };

  const getAntibiotics = (): HealthSystemDomainInjectionsResultSupplyInfo[] => {
    return supplyInfo.filter((x) => x.supply.supplyType === MedicalSupplyType.Antibiotic);
  };

  return (
    <>
      {/* Vaccination & Medication table */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small" aria-label={t('f-strat-vacc-data')}>
          <TableHead>
            <TableRow>
              <TableCell align="right">{t('f-strat-vacc')}</TableCell>
              {showPerAnimalColumns && <TableCell>{t('f-loc-view-avg-age')}</TableCell>}
              <TableCell>{t('f-strat-total-injected')}</TableCell>
              <TableCell>{t('f-strat-total-cc-dose-used')}</TableCell>
              <TableCell>{t('f-strat-avg-cc-dose-used')}</TableCell>

              {isSowOrGilt && (
                <>
                  <TableCell>{t('f-com-days-preg')}</TableCell>
                  <TableCell>{t('f-com-days-lact')}</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Vaccinations */}
            {getVaccinations().map((row) => (
              <TableRow key={row.supply.id} hover>
                <TableCell scope="row" align="right">
                  {row.supply.number}
                </TableCell>
                {showPerAnimalColumns && <TableCell>{row.averageAge?.toFixed(2)}</TableCell>}
                <TableCell>{row.totalInjections}</TableCell>
                <TableCell>{row.totalCCDoseUsed?.toFixed(2)}</TableCell>
                <TableCell>{row.averageCCDoseUsed?.toFixed(2)}</TableCell>

                {isSowOrGilt && (
                  <>
                    <TableCell>{row.pregnancyDay}</TableCell>
                    <TableCell>{row.lactatingDay}</TableCell>
                  </>
                )}
              </TableRow>
            ))}

            {getVaccinations().length === 0 && (
              <TableRow>
                <TableCell variant="footer" align="right">
                  <Chip className={classes.tableInfoChip} label={t('f-strat-vacc-no-data')} />
                </TableCell>
                {showPerAnimalColumns && <TableCell></TableCell>}
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                {isSowOrGilt && (
                  <>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </>
                )}
              </TableRow>
            )}

            {/* Medications */}
            <TableRow>
              <TableCell align="right" variant="head">
                {t('f-strat-med')}
              </TableCell>
              {showPerAnimalColumns && <TableCell></TableCell>}
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              {isSowOrGilt && (
                <>
                  <TableCell variant="head">{t('f-com-days-preg')}</TableCell>
                  <TableCell variant="head">{t('f-com-days-lact')}</TableCell>
                </>
              )}
            </TableRow>

            {getAntibiotics().map((row) => (
              <TableRow key={row.supply.id} hover>
                <TableCell scope="row" align="right">
                  {row.supply.number}
                </TableCell>
                {showPerAnimalColumns && <TableCell>{row.averageAge?.toFixed(2)}</TableCell>}
                <TableCell>{row.totalInjections}</TableCell>
                <TableCell>{row.totalCCDoseUsed?.toFixed(2)}</TableCell>
                <TableCell>{row.averageCCDoseUsed?.toFixed(2)}</TableCell>

                {isSowOrGilt && (
                  <>
                    <TableCell>{row.pregnancyDay}</TableCell>
                    <TableCell>{row.lactatingDay}</TableCell>
                  </>
                )}
              </TableRow>
            ))}

            {getAntibiotics().length === 0 && (
              <TableRow>
                <TableCell variant="footer" align="right">
                  <Chip className={classes.tableInfoChip} label={t('f-strat-med-no-data')} />
                </TableCell>
                {showPerAnimalColumns && <TableCell></TableCell>}
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>

                {isSowOrGilt && (
                  <>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default VaccinationAndMedicationDataTable;
